import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APIKey, apiRoutes } from 'constants/apiKeys';
import baseFetcher from 'shared/helpers/baseFetcher';
import { Step } from 'types/step';

export interface ECoachSession {
  id: string;
  title: string;
  goal?: string;
  resolution_status: 'draft' | 'active' | 'resolved' | 'skipped';
  created_at: string;
  modified_at: string;
  steps?: Step[];
}

export function useECoachSessions() {
  return useQuery<ECoachSession[]>({
    queryKey: [APIKey.ECOACH_SESSIONS],
    queryFn: () => baseFetcher(apiRoutes.ecoachSessions()),
  });
}

export function useECoachSession(id?: string, skipResolution = false) {
  return useQuery<ECoachSession>({
    queryKey: [APIKey.ECOACH_SESSIONS, { id }],
    queryFn: () =>
      baseFetcher(
        skipResolution
          ? `${apiRoutes.ecoachSession(id)}?resolution=false`
          : apiRoutes.ecoachSession(id)
      ),
    enabled: !!id,
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation<ECoachSession, unknown, void>({
    mutationFn: () =>
      baseFetcher(apiRoutes.ecoachSessions(), { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APIKey.ECOACH_SESSIONS] });
    },
  });
}

type UpdateStatusVariables = { id: string; status: string };

export function useUpdateSessionStatus() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, UpdateStatusVariables>({
    mutationFn: ({ id, status }) =>
      baseFetcher(apiRoutes.ecoachSessionStatus(id), {
        method: 'PUT',
        data: { resolution_status: status },
      }),
    onSuccess: (data, { id, status }) => {
      queryClient.invalidateQueries({ queryKey: [APIKey.ECOACH_SESSIONS] });
      queryClient.setQueryData(
        [APIKey.ECOACH_SESSIONS, { id }],
        (oldData: ECoachSession[]) =>
          oldData.map((session) =>
            session.id === id
              ? { ...session, resolution_status: status }
              : session
          )
      );
    },
  });
}

export function useHideSession() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (id) =>
      baseFetcher(apiRoutes.ecoachSessionHide(id), { method: 'PUT' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APIKey.ECOACH_SESSIONS] });
    },
  });
}
